import React, { useEffect, useState } from "react";
import { Col, Container, ProgressBar, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import StarRating from "components/common/StarRating";
import NoDataFound from "components/common/NoDataFound";
import calender from "assets/images/icons/calendar.svg";
import upDownArrow from "assets/images/icons/upDownArrow.svg";
import Paginations from "components/common/Paginations";
import { useDispatch, useSelector } from "react-redux";
import Spinners from "components/common/Spinners";
import {
  getDashboardRatingHistory,
  ratingDashboardHistory,
} from "store/actions/dashboardSlice";
import {
  orderByOptions,
  orderStatusTypes,
} from "components/constants/StatusKeys";
import { getMinMaxDate, getRelativeTimeFromNow } from "components/common/Utils";
import {
  orderSpaceType,
  spaceTypeOptions,
} from "components/constants/DropdownOptions";
import Filters from "components/common/Filters";
import { getBranchListWithCity } from "store/actions/branchSlice";
import excelIcon from "assets/images/icons/excelIcon.svg";
import { Download } from "lucide-react";
import { showAlert } from "store/reducers/alertReducer";
import { useNavigate } from "react-router-dom";
import ReviewHistoryDashboardShimmer from "components/ShimmerEffects/ReviewHistoryDashboardShimmer";
import TableShimmer from "components/ShimmerEffects/CommonShimmer/TableShimmer";

const ReviewHistory = () => {
  const { t } = useTranslation();
  document.title = t("Reviews History | HubHouse");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    loading,
    dashboardRatingHistory,
    perPage,
    exportReviewHistory,
    isLoading,
  } = useSelector((state) => state.Dashboards);

  const [selectedFilters, setSelectedFilters] = useState({
    orderBy: -1,
    spaceTypeInt: [],
    branches: [],
  });

  const [dates, setDates] = useState(null);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    dispatch(getBranchListWithCity());
    getAllData();
  }, []);

  const getAllData = (page, action) => {
    const { actionType } = action || {}; // SEARCH, RESET, RESET_KEYWORD
    const { orderBy, spaceTypeInt, branches } = selectedFilters;

    const isReset = actionType === "RESET";
    const payload = {
      spaceTypeInt: isReset ? [] : spaceTypeInt,
      branches: isReset ? [] : branches,
      dateRange: isReset ? {} : getMinMaxDate(dates),
      pageNo: page || 1,
      perPage: perPage,
      orderBy: isReset ? -1 : orderBy,
      keyword: ["RESET", "RESET_KEYWORD"].includes(actionType)
        ? ""
        : keyword || "",
    };
    dispatch(getDashboardRatingHistory(payload));
  };

  const handleDownload = () => {
    const { spaceTypeInt, branches } = selectedFilters;

    const payload = {
      spaceTypeInt,
      branches,
      dateRange: getMinMaxDate(dates),
      keyword,
    };
    dispatch(ratingDashboardHistory(payload))
      .then((action) => {
        const { statusCode, success, message, data } = action?.payload;
        if (statusCode === 200 && success) {
          const { url } = data || {};
          window.open(url, "_blank");
          dispatch(showAlert({ text: message, type: "success" }));
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleReset = () => {
    setKeyword("");
    setDates([]);
    setSelectedFilters({ orderBy: -1 });
    getAllData(1, { actionType: "RESET", spaceTypeInt: [], branches: [] });
  };

  const filters = [
    {
      key: "orderBy",
      placeholder: "Sort By",
      options: orderByOptions,
      type: "singleSelect",
    },
    {
      key: "spaceTypeInt",
      placeholder: "Space Type",
      options: orderSpaceType,
      type: "multiSelect",
    },
    {
      key: "branches",
      type: "branchCityFilter",
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content reviews_dashboard">
        {loading ? (
          <>
            <ReviewHistoryDashboardShimmer />
            <TableShimmer />
          </>
        ) : (
          <Container fluid>
            <Row>
              <div className="g_between">
                <h1 className="main_title">{t("Reviews")}</h1>
                <div className="download_pdf_div d-flex gap-4">
                  <div className="d-flex gap-2">
                    <img src={excelIcon} alt="pdf" width={25} height={25} />
                    <div>
                      <h2 className="fs_12">{t("Download Reviews")}</h2>
                      {exportReviewHistory?.size && (
                        <p className="fs_12 text_gray">
                          {exportReviewHistory?.size} KB
                        </p>
                      )}
                    </div>
                  </div>
                  <Download
                    className={`c_p ${isLoading ? "opacity-50" : ""}`}
                    size={20}
                    onClick={handleDownload}
                  />
                </div>
              </div>
            </Row>
            <div className="inner_wrapper">
              <Filters
                keyword={keyword}
                setKeyword={setKeyword}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                dates={dates}
                setDates={setDates}
                filters={filters}
                onSearch={getAllData}
                onReset={handleReset}
                className="filter_2"
              />
              <Row className="common_row">
                <Col lg={12} className="review_history">
                  <h2 className="fs_20">{t("Review History")}</h2>
                  <Row className="mt-4 px-3">
                    <Col lg={4}>
                      <h2 className="fs_50">
                        {dashboardRatingHistory?.averageRating?.toFixed(1) ?? 0}
                      </h2>
                      <StarRating
                        rating={dashboardRatingHistory?.averageRating}
                      />
                      <p className="fs_14 text_gray mt-3">
                        ({dashboardRatingHistory?.totalRatings ?? 0}{" "}
                        {t("Reviews")})
                      </p>
                    </Col>
                    <Col lg={8}>
                      {dashboardRatingHistory?.stars?.map((rating) => (
                        <div className="d-flex gap-4">
                          <h6 className="fs_12 w_fit_content">
                            {rating.value} {t("Star")}
                          </h6>
                          <div className="w-100 custom_progressbar">
                            <ProgressBar
                              now={rating.percent}
                              className={
                                rating.value === 1 ? "negative_rating" : ""
                              }
                            />
                          </div>
                          <h6 className="fs_12">{rating.count}</h6>
                        </div>
                      ))}
                    </Col>
                  </Row>
                </Col>
                <Col lg={12} className="my-3">
                  <div className="common_table_style">
                    {dashboardRatingHistory?.history?.length === 0 ? (
                      <NoDataFound />
                    ) : (
                      <>
                        <Table>
                          <thead className="t_head">
                            <tr>
                              <th>
                                <div className="d-flex align-items-center gap-1">
                                  <img
                                    src={calender}
                                    alt="icon"
                                    width={16}
                                    height={16}
                                  />
                                  {t("Date")}
                                </div>
                              </th>
                              <th>{t("Status")}</th>
                              <th>{t("Booking No")}</th>
                              <th>{t("User")}</th>
                              <th>{t("Number")}</th>
                              <th>{t("Booking")}</th>
                              <th>
                                <div className="d-flex align-items-center gap-1">
                                  <img
                                    src={upDownArrow}
                                    alt="icon"
                                    width={16}
                                    height={16}
                                  />
                                  {t("Rating")}
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {dashboardRatingHistory?.history?.map((item) => (
                              <tr
                                key={item._id}
                                onClick={() =>
                                  navigate(
                                    `/bookings/bookings/booking-details?id=${item?.bookingId}`
                                  )
                                }
                                className="c_p"
                              >
                                <td>
                                  <p className="w_fit_content">
                                    {getRelativeTimeFromNow(
                                      item?.createdAt || "-"
                                    )}
                                  </p>
                                </td>
                                <td>
                                  <p
                                    className={`w_fit_content ${
                                      orderStatusTypes?.find(
                                        (option) =>
                                          option?.value === item?.bookingStatus
                                      )?.class
                                    }`}
                                  >
                                    {t(
                                      orderStatusTypes?.find(
                                        (option) =>
                                          option?.value === item?.bookingStatus
                                      )?.name
                                    )}
                                  </p>
                                </td>
                                <td>#{item?.bookingNo || "-"}</td>
                                <td>{item?.user?.name || "-"}</td>
                                <td>{item?.user?.phone || "-"}</td>
                                <td>
                                  <div style={{ minWidth: 175 }}>
                                    {t(
                                      spaceTypeOptions?.find(
                                        (option) =>
                                          option.value ===
                                          item?.space?.spaceType
                                      )?.name
                                    )}{" "}
                                    {"-"} {item?.space?.name}
                                    <p className="fs_12 text_gray">
                                      {item?.space?.city?.name},{" "}
                                      {item?.space?.branch?.name}
                                    </p>
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    {item?.rating || "-"}
                                    <StarRating
                                      rating={item?.rating}
                                      starRatedColor={
                                        item?.rating > 2 ? "#C4D26E" : "#FF6363"
                                      }
                                    />
                                  </div>
                                  <p
                                    className="fs_14 mt-2"
                                    style={{ minWidth: 200 }}
                                  >
                                    {item?.feedback || "-"}
                                  </p>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        <Paginations
                          currentPage={dashboardRatingHistory?.currentPage}
                          totalPages={dashboardRatingHistory?.totalPages}
                          onPageChange={(page) => getAllData(page)}
                          totalCount={perPage}
                        />
                      </>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default ReviewHistory;
